import {
	Button,
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@monbits/ui";
import { KeyRound, MoreVertical, Trash2 } from "lucide-react";
import * as React from "react";
import { toast } from "sonner";
import { ConfirmationDialog } from "src/components/confirmation-dialog";
import DrawerForm from "src/components/drawer-form/drawer-form";
import { Loader } from "src/components/loader/loader";
import PasswordLabel from "src/components/password-label/password-label";
import TimeLabel from "src/components/time-label/time-label";
import TokenCreateForm from "src/components/token-create-form/token-create-form";
import tokenQueries from "src/queries/token-quries";
import type { MerchantToken } from "src/types";
import { errorMessage } from "src/utils/error-message";

interface Props {
	openPopupFunc: (title: string, desc: string) => void;
}

const ApiKeysSection: React.FC<Props> = ({ openPopupFunc }) => {
	const listTokens = tokenQueries.listTokens();
	const createToken = tokenQueries.createToken();
	const deleteToken = tokenQueries.deleteToken();
	const [isCreatingTokenFormOpen, setIsCreatingTokenFormOpen] =
		React.useState(false);
	const [isFormSubmitting, setIsFormSubmitting] = React.useState(false);
	const [tokenPendingDeletion, setTokenPendingDeletion] =
		React.useState<MerchantToken | null>(null);

	const isLoading =
		listTokens.isLoading ||
		listTokens.isFetching ||
		deleteToken.isPending ||
		createToken.isPending;

	const tokens = listTokens.data ?? [];

	const confirmDeleteToken = async () => {
		if (!tokenPendingDeletion) return;
		const token = tokenPendingDeletion;
		try {
			await deleteToken.mutateAsync(token.id);
			openPopupFunc("API key deleted", `'${token.name}' has been removed.`);
		} catch (error) {
			toast.error(errorMessage(error, "Failed to delete API key"));
		}
	};

	const uploadCreatedToken = async (tokenName: string) => {
		try {
			setIsFormSubmitting(true);
			await createToken.mutateAsync(tokenName);
			setIsCreatingTokenFormOpen(false);
			openPopupFunc("API key created", `'${tokenName}' is ready to use.`);
		} catch (error) {
			toast.error(errorMessage(error, "Failed to create API key"));
		} finally {
			setIsFormSubmitting(false);
		}
	};

	return (
		<>
			<Card>
				<CardHeader className="pb-2">
					<div className="flex items-start justify-between gap-3">
						<div className="space-y-1">
							<CardTitle className="text-base font-medium">API keys</CardTitle>
							<CardDescription className="text-xs">
								Use a key to authenticate every request.
							</CardDescription>
						</div>
						<Button
							size="sm"
							className="flex-shrink-0"
							onClick={() => setIsCreatingTokenFormOpen(true)}
						>
							Create key
						</Button>
					</div>
				</CardHeader>
				<CardContent>
					{isLoading ? (
						<div className="flex items-center justify-center py-12">
							<Loader size="md" />
						</div>
					) : tokens.length === 0 ? (
						<div className="flex flex-col items-center gap-3 py-12 text-center">
							<div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted">
								<KeyRound
									aria-hidden="true"
									className="h-4 w-4 text-muted-foreground"
								/>
							</div>
							<div className="space-y-1">
								<p className="text-sm font-medium">No API keys yet</p>
								<p className="text-xs text-muted-foreground">
									Create one to start authenticating requests.
								</p>
							</div>
							<Button
								size="sm"
								className="mt-1"
								onClick={() => setIsCreatingTokenFormOpen(true)}
							>
								Create key
							</Button>
						</div>
					) : (
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead className="w-[15%]">Created</TableHead>
									<TableHead className="w-[20%]">Name</TableHead>
									<TableHead>Key</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{tokens.map((record) => (
									<TableRow key={record.id}>
										<TableCell>
											<TimeLabel time={record.createdAt} />
										</TableCell>
										<TableCell className="font-medium">
											<span
												className="block max-w-[200px] truncate"
												title={record.name}
											>
												{record.name}
											</span>
										</TableCell>
										<TableCell>
											<div className="flex items-center justify-between">
												<PasswordLabel
													text={record.token}
													popupFunc={openPopupFunc}
												/>
												<DropdownMenu>
													<DropdownMenuTrigger asChild>
														<Button
															variant="ghost"
															size="icon"
															aria-label="API key actions"
														>
															<MoreVertical className="h-4 w-4" />
														</Button>
													</DropdownMenuTrigger>
													<DropdownMenuContent align="end">
														<DropdownMenuItem
															className="cursor-pointer text-destructive focus:text-destructive"
															disabled={deleteToken.isPending}
															onSelect={() => setTokenPendingDeletion(record)}
														>
															<Trash2 className="mr-2 h-4 w-4" />
															Delete
														</DropdownMenuItem>
													</DropdownMenuContent>
												</DropdownMenu>
											</div>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					)}
				</CardContent>
			</Card>

			<DrawerForm
				title="New API key"
				description="Name it after the integration that will use it."
				isOpen={isCreatingTokenFormOpen}
				onClose={() => setIsCreatingTokenFormOpen(false)}
				isSubmitting={isFormSubmitting}
				submitText="Create key"
				width={512}
			>
				<TokenCreateForm
					onFinish={uploadCreatedToken}
					isFormSubmitting={isFormSubmitting}
				/>
			</DrawerForm>

			<ConfirmationDialog
				open={tokenPendingDeletion !== null}
				onOpenChange={(open) => {
					if (!open) setTokenPendingDeletion(null);
				}}
				onConfirm={confirmDeleteToken}
				title="Delete API key?"
				description={
					tokenPendingDeletion
						? `'${tokenPendingDeletion.name}' will stop working immediately. This can't be undone.`
						: ""
				}
				confirmText="Delete"
				variant="destructive"
			/>
		</>
	);
};

export default ApiKeysSection;
